import React, { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Database, Calendar, X, Plus, Minus, FileSpreadsheet } from 'lucide-react';

export default function DataTable({
  data = [],
  columns = [],
  loading = false,
  searchKeys = [],
  dateKey,
  pageSize = 15,
  actions,
  emptyText = 'No records found',
  emptySubtext,
  emptyIcon,
  onRowClick,
  isRowSelected,
  hideSearch = false,
  noDateFilter = false,
  // Tree view: when expandedRender is given, each row gets a +/− toggle in
  // the first column; expanding renders expandedRender(row) full-width
  // beneath the row. onExpand(row) fires the first time a row is opened
  // (use it to lazily fetch the child data). rowKey(row) must return a
  // stable id for tracking open rows across pages.
  expandedRender,
  onExpand,
  rowKey,
  // Excel export: when exportName is set, the toolbar shows an "Export Excel"
  // button that downloads the FILTERED rows as <exportName>-<date>.xlsx.
  // Columns with keys starting with "_" (actions) are skipped; a column can
  // supply exportValue(row) for a custom raw value, or set noExport: true.
  exportName,
}) {
  const [search, setSearch]     = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [page, setPage]         = useState(1);
  const [expanded, setExpanded] = useState({});

  const keyOf = (row, ri) => (rowKey ? rowKey(row) : ri);
  const toggleExpand = (row, ri) => {
    const k = keyOf(row, ri);
    setExpanded(prev => {
      const opening = !prev[k];
      if (opening && onExpand) onExpand(row);
      return { ...prev, [k]: opening };
    });
  };
  const colCount = columns.length + (expandedRender ? 1 : 0);

  // Auto-detect date column if dateKey is not explicitly passed
  const activeDateKey = dateKey || useMemo(() => {
    if (noDateFilter) return null;
    const colMatch = columns.find(c => ['date', 'purchasedate', 'invoicedate', 'createddate', 'createdat', 'supplierinvdate'].includes(String(c.key || c.header).toLowerCase().replace(/[^a-z]/g, '')));
    if (colMatch?.key) return colMatch.key;
    if (data.length > 0) {
      const sample = data[0];
      const keys = Object.keys(sample);
      const matchedKey = keys.find(k => ['date', 'purchasedate', 'invoicedate', 'createddate', 'createdat', 'supplierinvdate'].includes(k.toLowerCase()));
      if (matchedKey) return matchedKey;
    }
    return null;
  }, [columns, data, dateKey, noDateFilter]);

  const hasDateColumn = Boolean(!noDateFilter && (activeDateKey || data.some(r => r.purchaseDate || r.invoiceDate || r.date || r.createdDate || r.supplierInvDate)));

  const filtered = useMemo(() => {
    let result = data;

    // Date range filter
    if (fromDate || toDate) {
      result = result.filter(row => {
        const dKey = activeDateKey || (row.purchaseDate ? 'purchaseDate' : row.invoiceDate ? 'invoiceDate' : row.date ? 'date' : row.createdDate ? 'createdDate' : 'supplierInvDate');
        const rawDate = row[dKey];
        if (!rawDate) return true;
        const dStr = String(rawDate).split('T')[0];
        if (fromDate && dStr < fromDate) return false;
        if (toDate && dStr > toDate) return false;
        return true;
      });
    }

    // Text search filter
    if (!search.trim()) return result;
    const q = search.toLowerCase();
    return result.filter(row =>
      searchKeys.some(key => String(row[key] ?? '').toLowerCase().includes(q))
    );
  }, [data, search, searchKeys, fromDate, toDate, activeDateKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageData   = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goTo = (p) => setPage(Math.max(1, Math.min(totalPages, p)));
  const handleSearch = (v) => { setSearch(v); setPage(1); };

  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    const exportCols = columns.filter(c => c.key && !String(c.key).startsWith('_') && !c.noExport);
    const isDateKey = (k) => String(k).toLowerCase().includes('date');
    const cellValue = (col, row) => {
      if (col.exportValue) return col.exportValue(row);
      const v = row[col.key];
      if (v == null) return '';
      if (isDateKey(col.key) && v) {
        const d = new Date(v);
        if (!isNaN(d)) return d.toLocaleDateString('en-IN');
      }
      return v;
    };
    const aoa = [
      exportCols.map(c => c.header),
      ...filtered.map(row => exportCols.map(col => cellValue(col, row))),
    ];
    // Grand-total row mirrors the on-screen footer where columns define totals
    if (columns.some(c => c.total)) {
      aoa.push(exportCols.map((c, i) => {
        if (c.total) {
          const t = c.total(filtered);
          // totals may return JSX for styling — recompute plain numbers where possible
          return (typeof t === 'string' || typeof t === 'number') ? t
            : filtered.reduce((s, r) => s + Number(r[c.key] || 0), 0);
        }
        return i === 0 ? 'Grand Total' : '';
      }));
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = exportCols.map(c => ({ wch: Math.max(12, String(c.header).length + 4) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${exportName}-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div>
      {/* Toolbar */}
      {!hideSearch && (
        <div className="table-toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', flex: 1 }}>
            <div className="search-box">
              <Search size={15} />
              <input
                className="form-control"
                placeholder="Search..."
                value={search}
                onChange={e => handleSearch(e.target.value)}
              />
            </div>

            {/* Date Range Filter */}
            {hasDateColumn && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', padding: '4px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                <Calendar size={14} style={{ color: '#64748b' }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Date:</span>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={e => { setFromDate(e.target.value); setPage(1); }}
                  style={{ height: 30, fontSize: 12, padding: '0 6px', width: 125, background: '#fff' }}
                  title="From Date"
                />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>to</span>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={e => { setToDate(e.target.value); setPage(1); }}
                  style={{ height: 30, fontSize: 12, padding: '0 6px', width: 125, background: '#fff' }}
                  title="To Date"
                />
                {(fromDate || toDate) && (
                  <button
                    type="button"
                    onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}
                    style={{ background: '#fee2e2', border: 'none', color: '#ef4444', borderRadius: 4, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: 2 }}
                    title="Clear date filter"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {exportName && (
              <button type="button" className="btn btn-ghost" onClick={exportExcel}
                disabled={loading || filtered.length === 0}
                title="Download the filtered rows as an Excel file">
                <FileSpreadsheet size={15} /> Export Excel
              </button>
            )}
            {actions}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {expandedRender && <th style={{ width: 36 }} />}
              {columns.map((col, i) => (
                <th key={i} style={{ textAlign: col.align || 'left', width: col.width }}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount}>
                  <div className="page-loader" style={{ height: 140 }}>
                    <div className="spinner" />
                    <span>Loading data…</span>
                  </div>
                </td>
              </tr>
            ) : pageData.length === 0 ? (
              <tr>
                <td colSpan={colCount}>
                  <div className="empty-state">
                    {emptyIcon || <Database size={40} />}
                    <h4>{(search || fromDate || toDate) ? `No results for applied filter` : emptyText}</h4>
                    {(search || fromDate || toDate)
                      ? <p>Try clearing your search or date range filter</p>
                      : emptySubtext ? <p>{emptySubtext}</p> : emptyText && emptyText !== 'No records found' ? null : <p>Add your first record to get started</p>
                    }
                  </div>
                </td>
              </tr>
            ) : (
              pageData.map((row, ri) => {
                const selected = isRowSelected ? isRowSelected(row) : false;
                const rk = keyOf(row, ri);
                const isOpen = expandedRender ? !!expanded[rk] : false;
                return (
                  <React.Fragment key={rk}>
                    <tr
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      style={{
                        cursor: onRowClick ? 'pointer' : undefined,
                        background: selected ? 'var(--primary-50)' : isOpen ? 'var(--silver-50, #f8fafc)' : undefined,
                        outline: selected ? '1.5px solid var(--primary-300)' : undefined,
                      }}
                    >
                      {expandedRender && (
                        <td style={{ textAlign: 'center', width: 36 }}>
                          <button
                            type="button"
                            title={isOpen ? 'Hide items' : 'Show items'}
                            onClick={e => { e.stopPropagation(); toggleExpand(row, ri); }}
                            style={{
                              width: 22, height: 22, borderRadius: 6, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              border: `1.5px solid ${isOpen ? 'var(--primary-300, #93c5fd)' : 'var(--silver-300, #cbd5e1)'}`,
                              background: isOpen ? 'var(--primary-50, #eff6ff)' : '#fff',
                              color: isOpen ? 'var(--primary-600, #2563eb)' : 'var(--silver-500, #64748b)',
                            }}
                          >
                            {isOpen ? <Minus size={13} /> : <Plus size={13} />}
                          </button>
                        </td>
                      )}
                      {columns.map((col, ci) => (
                        <td key={ci} style={{ textAlign: col.align || 'left' }}>
                          {col.render ? col.render(row) : row[col.key]}
                        </td>
                      ))}
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={colCount} style={{ padding: 0, background: 'var(--silver-50, #f8fafc)' }}>
                          {expandedRender(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {/* Grand total — rendered when any column defines total(rows).
              Totals cover ALL filtered rows, not just the current page. */}
          {!loading && filtered.length > 0 && columns.some(c => c.total) && (
            <tfoot>
              <tr style={{ background: 'var(--navy-900, #0f172a)' }}>
                {expandedRender && <td style={{ padding: '11px 12px' }} />}
                {columns.map((col, i) => (
                  <td key={i} style={{ textAlign: col.align || 'left', padding: '11px 12px', fontSize: 13, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', fontFamily: col.total ? 'var(--mono)' : undefined }}>
                    {col.total
                      ? col.total(filtered)
                      : (i === 0 ? <>Grand Total <span style={{ fontWeight: 500, fontSize: 11.5, color: '#94a3b8' }}>({filtered.length} record{filtered.length !== 1 ? 's' : ''})</span></> : '')}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {filtered.length > pageSize && (
        <div className="flex-between mt-4" style={{ padding: '12px 16px', flexWrap: 'wrap', gap: 8 }}>
          <span className="text-muted text-sm">
            Showing <strong>{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)}</strong> of <strong>{filtered.length}</strong> records
          </span>
          <div className="pagination">
            <button className="page-btn" onClick={() => goTo(1)} disabled={safePage === 1}>
              <ChevronsLeft size={13} />
            </button>
            <button className="page-btn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1}>
              <ChevronLeft size={13} />
            </button>
            {(() => {
              let start = Math.max(1, safePage - 2);
              let end = Math.min(totalPages, start + 4);
              if (end - start < 4) {
                start = Math.max(1, end - 4);
              }
              const pages = [];
              for (let p = start; p <= end; p++) {
                if (p >= 1 && p <= totalPages) pages.push(p);
              }
              return pages.map(p => (
                <button key={p} className={`page-btn${safePage === p ? ' active' : ''}`} onClick={() => goTo(p)}>
                  {p}
                </button>
              ));
            })()}
            <button className="page-btn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages}>
              <ChevronRight size={13} />
            </button>
            <button className="page-btn" onClick={() => goTo(totalPages)} disabled={safePage === totalPages}>
              <ChevronsRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
